import React, { useRef, useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import {
  OVERLAY_SIZES,
  DRAWER_WIDTH_MAP,
  OVERLAY_BASE_Z_INDEX,
  OVERLAY_Z_INDEX_STEP,
  DRAWER_TRANSITION_DURATION,
} from "@/components/overlays/constants/overlayDefaults";

/**
 * OverlayDrawer — custom Tailwind-based sliding panel for the overlay system.
 *
 * Mirrors the legacy SlideOver DOM structure so existing drawer components
 * can be migrated without markup changes.
 *
 * Two-phase animation:
 *   mount  → slide-in  (translate-x-full → translate-x-0)
 *   close  → slide-out (translate-x-0 → translate-x-full), then unmount
 */
export default function OverlayDrawer({ entry, stackIndex, isTopmost, onClose, onRemove }) {
  const removeTimerRef = useRef(null);
  const { key, Component, props, options, closing } = entry;

  const size = options?.size || OVERLAY_SIZES.MD;
  const width = DRAWER_WIDTH_MAP[size] || DRAWER_WIDTH_MAP[OVERLAY_SIZES.MD];
  const backdropZ = OVERLAY_BASE_Z_INDEX + stackIndex * OVERLAY_Z_INDEX_STEP;
  const panelZ = backdropZ + 1;

  // Controls the CSS transition. Starts false (off-screen), becomes true
  // after mount (slide in), resets to false on close (slide out).
  const [visible, setVisible] = useState(false);

  // Phase 1 — slide in after initial render
  useEffect(() => {
    const frame = requestAnimationFrame(() => setVisible(true));
    return () => cancelAnimationFrame(frame);
  }, []);

  // Phase 2 — when closing flag flips, slide out then remove
  useEffect(() => {
    if (closing) {
      queueMicrotask(() => setVisible(false));
      removeTimerRef.current = setTimeout(() => {
        onRemove(key);
      }, DRAWER_TRANSITION_DURATION + 50);
    }
    return () => clearTimeout(removeTimerRef.current);
  }, [closing, key, onRemove]);

  const handleClose = useCallback(() => {
    onClose(key);
  }, [onClose, key]);

  const handleBackdropClick = useCallback(() => {
    if (isTopmost) {
      handleClose();
    }
  }, [isTopmost, handleClose]);

  return (
    <>
      {/* Backdrop */}
      <div
        className={classNames(
          "fixed inset-0 bg-gray-500 transition-opacity ease-in-out",
          visible ? "opacity-75" : "opacity-0",
        )}
        style={{ zIndex: backdropZ, transitionDuration: `${DRAWER_TRANSITION_DURATION}ms` }}
        onClick={handleBackdropClick}
        aria-hidden="true"
      />

      {/* Panel container */}
      <div
        role="dialog"
        aria-modal="true"
        className="fixed inset-0 overflow-hidden"
        style={{ zIndex: panelZ, pointerEvents: "none" }}
      >
        <div className="absolute inset-0 overflow-hidden">
          <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
            <div
              className={classNames(
                "pointer-events-auto w-screen transition-transform ease-in-out",
                visible ? "translate-x-0" : "translate-x-full",
              )}
              style={{ maxWidth: width, transitionDuration: `${DRAWER_TRANSITION_DURATION}ms` }}
            >
              <div
                className={classNames(
                  "flex h-full flex-col bg-white shadow-xl overflow-y-auto",
                  "dark:bg-slate-800",
                  "[&>div:first-child]:h-full",
                )}
              >
                <Component {...props} handleClose={handleClose} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

OverlayDrawer.propTypes = {
  entry: PropTypes.shape({
    key: PropTypes.string.isRequired,
    Component: PropTypes.elementType.isRequired,
    props: PropTypes.object,
    options: PropTypes.object,
    closing: PropTypes.bool,
  }).isRequired,
  stackIndex: PropTypes.number.isRequired,
  isTopmost: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

